/**
 * Storefront API client for pickup availability + delivery rates.
 *
 * Both the Find in store and Delivery estimate drawers need the SAME three things
 * resolved from one shopper input, so they share this module:
 *
 *   1. geocode(query)  -> { latitude, longitude, suburb, state, postcode }
 *   2. availability()  -> ProductVariant.storeAvailability, proximity-sorted by Shopify
 *   3. rateFor()       -> the shipping_rate metaobject price for a (suburb, state)
 *
 * WHY THE STOREFRONT API AND NOT LIQUID
 * `variant.store_availabilities` in Liquid is render-time only. These drawers respond
 * to user input (a typed postcode), so the list has to be re-fetched per query — hence
 * a JS-hydrated read. `storeAvailability(near:)` also makes SHOPIFY do the proximity
 * sort, which replaces the postcode-centroid + haversine approximation this drawer used
 * to do client-side.
 *
 * SCOPE REQUIREMENT
 * `storeAvailability` and `locations` both require the storefront token to hold
 * `unauthenticated_read_product_pickup_locations`. Without it the API returns
 * ACCESS_DENIED (an error), NOT an empty list — the two are distinguished in
 * `request()` below, because "no pickup locations enabled" is a legitimate empty
 * state while a scope failure is a misconfiguration the merchant must fix.
 *
 * EMPTY IS NOT AN ERROR
 * Shopify only returns locations with local pickup ENABLED in Settings > Shipping
 * and delivery. A store can hold the right scope and still return [] because no
 * showroom has pickup switched on yet. Callers must render an empty state, not fail.
 */
window.StoreAvailability = (function () {
  const cfg = window.storeAvailabilityConfig || {};

  // `routes.root_url` is "/" on a single-locale shop and "/en-au" on a localised one.
  // Naively concatenating the former yields "//api/..." — a PROTOCOL-RELATIVE url that
  // the browser resolves to host "api", so every request fails with "Failed to fetch".
  // Strip the trailing slash before joining.
  const root = String(cfg.root || '').replace(/\/+$/, '');
  const endpoint = cfg.token ? `${root}/api/${cfg.version || '2026-04'}/graphql.json` : null;

  const AVAILABILITY_QUERY = `
    query StoreAvailability($handle: String!, $near: GeoCoordinateInput, $first: Int!) {
      product(handle: $handle) {
        selectedOrFirstAvailableVariant {
          id
          storeAvailability(first: $first, near: $near) {
            nodes {
              available
              pickUpTime
              location {
                id
                name
                address {
                  address1
                  address2
                  city
                  province
                  provinceCode
                  zip
                  phone
                  latitude
                  longitude
                }
              }
            }
          }
        }
      }
    }
  `;

  /**
   * Single POST wrapper. Separates the three failure modes callers care about:
   * transport failure, a GraphQL scope/validation error, and a successful empty read.
   */
  async function request(query, variables) {
    if (!endpoint) {
      return { ok: false, reason: 'unconfigured', data: null };
    }

    let res;
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token,
        },
        body: JSON.stringify({ query, variables }),
      });
    } catch (e) {
      return { ok: false, reason: 'network', data: null };
    }

    if (!res.ok) return { ok: false, reason: 'http', data: null };

    const json = await res.json().catch(() => null);
    if (!json) return { ok: false, reason: 'parse', data: null };

    if (json.errors && json.errors.length) {
      // A missing pickup-locations scope surfaces here, not as an empty list. Report it
      // distinctly so the drawer can stay silent for shoppers while still being
      // diagnosable — a scope error is a merchant misconfiguration, not a shopper state.
      const denied = json.errors.some((e) => e?.extensions?.code === 'ACCESS_DENIED');
      return { ok: false, reason: denied ? 'denied' : 'graphql', data: null, errors: json.errors };
    }

    return { ok: true, reason: null, data: json.data };
  }

  /**
   * Pickup availability for a product, proximity-sorted when an origin is supplied.
   * `near` is optional: without it Shopify returns its default order, which is what a
   * first open (no query yet) should show.
   */
  async function availability(handle, origin, first) {
    const variables = {
      handle,
      first: first || 10,
      near: origin ? { latitude: origin.latitude, longitude: origin.longitude } : null,
    };
    const out = await request(AVAILABILITY_QUERY, variables);
    if (!out.ok) return { ok: false, reason: out.reason, stores: [] };

    const nodes = out.data?.product?.selectedOrFirstAvailableVariant?.storeAvailability?.nodes || [];
    return { ok: true, reason: null, stores: nodes };
  }

  /**
   * Resolve a typed postcode/suburb to coordinates + locality via the app proxy.
   *
   * Proxied deliberately: the geocoding key must not ship in page source, and the proxy
   * reuses the SAME slug normalisation as the carrier service so the suburb string it
   * returns is guaranteed to match a shipping_rate metaobject handle. Resolving
   * client-side against a different geocoder would silently drift from checkout.
   *
   * Returns `matches[]` because one postcode maps to MANY suburbs at different rates
   * (2000 -> Sydney and Barangaroo). The caller must disambiguate rather than guess.
   */
  async function geocode(query) {
    if (!cfg.geocodeUrl) return { ok: false, reason: 'unconfigured', matches: [] };

    let res;
    try {
      res = await fetch(`${cfg.geocodeUrl}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: 'application/json' },
      });
    } catch (e) {
      return { ok: false, reason: 'network', matches: [] };
    }
    if (!res.ok) return { ok: false, reason: 'http', matches: [] };

    const json = await res.json().catch(() => null);
    if (!json) return { ok: false, reason: 'parse', matches: [] };

    return { ok: true, reason: null, matches: json.matches || [] };
  }

  /**
   * Type-ahead locality suggestions.
   *
   * Returns `{ placeId, label }` only. The suburb a rate depends on is resolved
   * server-side by `place()` from the opaque id, so the shopper always ends up on a
   * locality they actually chose — free text lets Google collapse ambiguity silently
   * ("Newtown" matches four states; a bare postcode resolves to one arbitrary suburb).
   *
   * `session` groups every keystroke plus the final `place()` call into ONE billable
   * Google session. Mint it once per search with `newSession()` and discard it after
   * `place()`, or each keystroke bills separately.
   */
  async function suggest(query, session) {
    if (!cfg.geocodeUrl) return { ok: false, reason: 'unconfigured', suggestions: [] };
    if (!query || query.trim().length < 2) return { ok: true, reason: null, suggestions: [] };

    const url = `${autocompleteUrl()}?q=${encodeURIComponent(query.trim())}${
      session ? `&session=${encodeURIComponent(session)}` : ''
    }`;

    let res;
    try {
      res = await fetch(url, { headers: { Accept: 'application/json' } });
    } catch (e) {
      return { ok: false, reason: 'network', suggestions: [] };
    }
    if (!res.ok) return { ok: false, reason: 'http', suggestions: [] };

    const json = await res.json().catch(() => null);
    if (!json) return { ok: false, reason: 'parse', suggestions: [] };

    return { ok: true, reason: null, suggestions: json.suggestions || [] };
  }

  /**
   * Resolve a chosen suggestion into a priced locality.
   * Same shape as one `geocode()` match, so callers have a single code path.
   */
  async function place(placeId, session) {
    if (!cfg.geocodeUrl) return { ok: false, reason: 'unconfigured', match: null };

    const url = `${placeUrl()}?id=${encodeURIComponent(placeId)}${
      session ? `&session=${encodeURIComponent(session)}` : ''
    }`;

    let res;
    try {
      res = await fetch(url, { headers: { Accept: 'application/json' } });
    } catch (e) {
      return { ok: false, reason: 'network', match: null };
    }
    if (!res.ok) return { ok: false, reason: 'http', match: null };

    const json = await res.json().catch(() => null);
    if (!json) return { ok: false, reason: 'parse', match: null };

    return { ok: true, reason: null, match: json.match || null };
  }

  // Sibling proxy paths, derived from the one configured geocode URL so the merchant
  // only has to set a single setting. Tolerates a trailing slash — a setting saved as
  // ".../geocode/" would otherwise silently derive the wrong path and 404.
  const proxyBase = () => String(cfg.geocodeUrl || '').replace(/\/+$/, '').replace(/\/geocode$/, '');
  const autocompleteUrl = () => `${proxyBase()}/autocomplete`;
  const placeUrl = () => `${proxyBase()}/place`;

  /** Fresh session token per search. Groups keystrokes + resolution into one charge. */
  function newSession() {
    if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  /**
   * Delivery price for a resolved locality.
   *
   * Reads a rate table embedded at render time. The rates live in `shipping_rate`
   * metaobjects with storefront PUBLIC_READ, keyed
   * `${postcode}-${slug(suburb)}-${slug(state)}` — the exact key the carrier service uses
   * at checkout, so the quoted figure and the charged figure agree.
   *
   * Postcode is part of the key because suburb+state is NOT unique in Australia and the
   * duplicates carry different prices (Alison NSW is $169 at 2420 but $129 at 2259).
   *
   * null = no entry for that locality, which is the "Delivery quote required" case the
   * carrier service also falls back to. It is NOT an error.
   */
  function rateFor(rates, postcode, suburb, state) {
    if (!rates || !postcode) return null;
    const key = `${slug(postcode)}-${slug(suburb)}-${slug(state)}`;
    return Object.prototype.hasOwnProperty.call(rates, key) ? rates[key] : null;
  }

  /** Mirror of workers/src/lib/slug.ts — must stay identical or every lookup misses. */
  function slug(s) {
    return (s || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  return {
    configured: !!endpoint,
    geocodeConfigured: !!cfg.geocodeUrl,
    availability,
    geocode,
    suggest,
    place,
    newSession,
    rateFor,
    slug,
  };
})();
